import { useEffect, useState } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import api, { getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { RISK_LEVEL_STYLE } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, ShieldAlert, AlertTriangle, BookMarked } from "lucide-react";

const LEVELS = ["Rendah", "Sedang", "Tinggi"];
const empty = { category: "", title: "", fs_line: "", account: "", assertion: "", inherent_risk: "Sedang", significant: false, fraud_related: false, description: "", typical_procedures: "", standards_ref: "", active: true };

export default function RiskLibrary() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ categories: [] });
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(empty);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/firm/risk-library").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/meta/risk-library").then((r) => setMeta(r.data)); }, []);

  const openNew = () => { setEditId(null); setF(empty); setOpen(true); };
  const openEdit = (it) => { setEditId(it.id); setF({ ...empty, ...it }); setOpen(true); };

  const save = async () => {
    if (!f.title.trim()) return toast.error("Judul risiko wajib diisi");
    if (!f.category) return toast.error("Kategori wajib dipilih");
    try {
      if (editId) await api.put(`/firm/risk-library/${editId}`, f);
      else await api.post("/firm/risk-library", f);
      setOpen(false); load(); toast.success("Pustaka risiko disimpan");
    } catch (e) { toast.error(getErrorMessage(e)); }
  };
  const del = async (id) => { if (!window.confirm("Hapus entri risiko ini?")) return; await api.delete(`/firm/risk-library/${id}`); load(); toast.success("Dihapus"); };

  const shown = filter === "all" ? items : items.filter((i) => i.category === filter);

  return (
    <Layout>
      <PageHeader breadcrumb="Metodologi" title="Pustaka Risiko" subtitle="Katalog risiko tingkat KAP — dapat dijadikan acuan penilaian risiko perikatan"
        actions={<Button size="sm" onClick={openNew} data-testid="rl-new"><Plus size={14} className="mr-1" />Risiko Baru</Button>} />
      <div className="p-6 space-y-4" data-testid="risk-library-page">
        <div className="flex items-center gap-3">
          <Label className="text-xs">Filter Kategori</Label>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-8 text-xs w-64" data-testid="rl-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Kategori</SelectItem>
              {meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">{shown.length} entri</span>
        </div>
        <div className="bg-white border border-border rounded-sm overflow-x-auto">
          <table className="w-full audit-table">
            <thead><tr><th>Kode</th><th>Kategori</th><th>Judul Risiko</th><th>FS Line / Akun</th><th>Asersi</th><th>Inheren</th><th>Sig/Fraud</th><th></th></tr></thead>
            <tbody>
              {shown.map((it) => (
                <tr key={it.id} data-testid={`rl-row-${it.id}`}>
                  <td className="font-mono text-[10px]">{it.code}</td>
                  <td className="text-xs">{it.category}</td>
                  <td className="text-xs max-w-xs"><div className="font-medium">{it.title}</div>{it.description && <div className="text-muted-foreground line-clamp-1">{it.description}</div>}</td>
                  <td className="text-xs">{it.fs_line}{it.account ? ` · ${it.account}` : ""}</td>
                  <td className="text-xs">{it.assertion}</td>
                  <td><span className={`text-[11px] px-2 py-0.5 border rounded-sm ${RISK_LEVEL_STYLE[it.inherent_risk] || ""}`}>{it.inherent_risk}</span></td>
                  <td className="whitespace-nowrap">
                    {it.significant && <span className="text-[10px] px-1 mr-1 border rounded-sm bg-amber-100 text-amber-800" title="Signifikan"><ShieldAlert size={11} className="inline" /></span>}
                    {it.fraud_related && <span className="text-[10px] px-1 border rounded-sm bg-red-100 text-red-800" title="Kecurangan"><AlertTriangle size={11} className="inline" /></span>}
                  </td>
                  <td className="whitespace-nowrap">
                    <Button variant="ghost" size="sm" className="h-6 px-1" onClick={() => openEdit(it)} data-testid={`rl-edit-${it.id}`}><Pencil size={13} /></Button>
                    <Button variant="ghost" size="sm" className="h-6 px-1 text-destructive" onClick={() => del(it.id)} data-testid={`rl-del-${it.id}`}><Trash2 size={13} /></Button>
                  </td>
                </tr>
              ))}
              {shown.length === 0 && <tr><td colSpan={8} className="text-center text-muted-foreground py-8"><BookMarked size={20} className="inline mb-1 opacity-40" /><div>Belum ada entri pustaka risiko.</div></td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="rl-dialog">
          <DialogHeader><DialogTitle>{editId ? "Ubah Entri Risiko" : "Entri Risiko Baru"}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <div><Label>Kategori</Label>
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                <SelectTrigger data-testid="rl-category"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
                <SelectContent>{meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Judul Risiko</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="rl-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>FS Line</Label><Input value={f.fs_line} onChange={(e) => setF({ ...f, fs_line: e.target.value })} /></div>
              <div><Label>Akun</Label><Input value={f.account} onChange={(e) => setF({ ...f, account: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Asersi</Label><Input value={f.assertion} onChange={(e) => setF({ ...f, assertion: e.target.value })} /></div>
              <div><Label>Risiko Inheren</Label>
                <Select value={f.inherent_risk} onValueChange={(v) => setF({ ...f, inherent_risk: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Deskripsi Risiko</Label><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
            <div><Label>Prosedur Tipikal</Label><Textarea rows={2} value={f.typical_procedures} onChange={(e) => setF({ ...f, typical_procedures: e.target.value })} /></div>
            <div><Label>Referensi Standar</Label><Input value={f.standards_ref} onChange={(e) => setF({ ...f, standards_ref: e.target.value })} placeholder="mis. SA 240, SA 315" /></div>
            <div className="flex gap-6 pt-1">
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.significant} onCheckedChange={(v) => setF({ ...f, significant: !!v })} data-testid="rl-significant" />Risiko Signifikan</label>
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.fraud_related} onCheckedChange={(v) => setF({ ...f, fraud_related: !!v })} data-testid="rl-fraud" />Terkait Kecurangan</label>
            </div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="rl-save">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
